import { useCallback, useEffect, useMemo, useState } from 'react';
import { Element, Form } from 'react-bulma-components';
import Button from '../../ui/button/button';
import FeedbackMessage from '../../ui/feedback-message/feedback-message';
import Modal from '../../ui/modal/modal';
import {
  initialFormData,
  RenameDeviceGroupFormBlurEvent,
  RenameDeviceGroupFormChangeEvent,
  RenameDeviceGroupFormData,
  validateForm,
} from './form';

export type RenameDeviceGroupModalProps = {
  show: boolean;
  onRenameDeviceGroup: (name: string) => void;
  onCancel: () => void;
  loading: boolean;
  currentName: string;
};

export function RenameDeviceGroupModal({
  show,
  onCancel,
  loading,
  onRenameDeviceGroup,
  currentName,
}: RenameDeviceGroupModalProps) {
  const [formData, setFormData] =
    useState<RenameDeviceGroupFormData>(initialFormData);

  useEffect(() => {
    if (show) {
      setFormData({
        name: {
          value: currentName,
          touched: false,
          error: null,
        },
      });
    }
  }, [currentName, show]);

  const newGroupName = useMemo(
    () => formData.name.value ?? '',
    [formData.name.value]
  );

  const onInputChange = useCallback(
    (event: RenameDeviceGroupFormChangeEvent) => {
      const newValue = event.target.value;
      const name = event.target.name as keyof RenameDeviceGroupFormData;
      setFormData({
        ...formData,
        [name]: { ...formData[name], value: newValue, touched: true },
      });
    },
    [formData, setFormData]
  );

  const onBlur = useCallback(
    (event: RenameDeviceGroupFormBlurEvent) => {
      const value = event.target.value;
      const name = event.target.name as keyof RenameDeviceGroupFormData;
      const error = validateForm[name]?.(value);
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          error,
        },
      });
    },
    [formData, setFormData]
  );

  const handleRenameDeviceGroupClick = useCallback(() => {
    const nameHasChanged = formData.name.value !== currentName;

    if (!nameHasChanged) {
      onCancel();
      return;
    }

    const formValidation = Object.keys(formData).reduce(
      (acc, key) => {
        const formDataKey = key as keyof RenameDeviceGroupFormData;
        const error = validateForm[formDataKey]?.(formData[formDataKey].value);
        setFormData((prev) => ({
          ...prev,
          [formDataKey]: { ...prev[formDataKey], error, touched: true },
        }));
        return {
          values: { ...acc.values, [key]: formData[formDataKey].value },
          errors: { ...acc.errors, [key]: error },
        };
      },
      { values: {}, errors: {} }
    );
    const isValidForm = Object.values(formValidation.errors).every(
      (fieldError) => !fieldError
    );

    if (isValidForm) {
      onRenameDeviceGroup(newGroupName);
    }
  }, [currentName, formData, newGroupName, onCancel, onRenameDeviceGroup]);

  return (
    <Modal show={show} title="Rename device group" titleClassName="mb-6">
      <Element
        paddingless
        display="flex"
        flexDirection="column"
        className="gap-8"
      >
        <p>
          <span>Please provide the new name for this device group. </span>
        </p>
        <p>
          <span>
            All users who have devices within this group will be notified.
          </span>
        </p>

        <Element>
          <Form.Control>
            <Form.Field>
              <Form.Label
                htmlFor="name"
                className="is-size-7 has-text-weight-normal"
              >
                New name
              </Form.Label>
              <Form.Control
                display="flex"
                flexDirection="row"
                className="gap-4"
              >
                <Form.Input
                  id="name"
                  name="name"
                  value={formData.name.value}
                  onChange={onInputChange}
                  onBlur={onBlur}
                />
              </Form.Control>
            </Form.Field>

            <FeedbackMessage>{formData.name.error}</FeedbackMessage>
          </Form.Control>
        </Element>

        <Element display="flex" justifyContent="space-between">
          <Button color="primary-dark" outlined onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color="primary-dark"
            onClick={handleRenameDeviceGroupClick}
            loading={loading}
          >
            <span>Rename</span>
          </Button>
        </Element>
      </Element>
    </Modal>
  );
}

export default RenameDeviceGroupModal;
